import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Checkbox,
  Divider,
  Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import useSimApi from "src/hooks/useSimApi";

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;
type Method = (typeof METHODS)[number];

const METHOD_COLORS: Record<Method, "success" | "primary" | "warning" | "info" | "error"> = {
  GET: "success",
  POST: "primary",
  PUT: "warning",
  PATCH: "info",
  DELETE: "error",
};

interface Endpoint {
  id: string;
  method: Method;
  path: string;
  status_code: number;
  response_headers: Record<string, string>;
  response_body: string;
  description: string;
  builtin?: boolean;
}

interface ScannedEndpoint {
  name?: string;
  description?: string | null;
  method: string;
  path: string;
  is_paginated?: boolean;
  tags?: string[] | null;
  response_example?: string | null;
}

const emptyForm = (): Omit<Endpoint, "id"> => ({
  method: "GET",
  path: "",
  status_code: 200,
  response_headers: {},
  response_body: "",
  description: "",
});

// ── Scan dialog ───────────────────────────────────────────────────────────────

type SimMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
type RequestFn = (path: string, method?: SimMethod, body?: unknown) => Promise<Response>;

interface ScanDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (endpoints: Omit<Endpoint, "id">[]) => Promise<void>;
  request: RequestFn;
}

type ScanPhase = "input" | "scanning" | "results";

const ScanDialog: React.FC<ScanDialogProps> = ({ open, onClose, onAdd, request }) => {
  const [url, setUrl] = useState("");
  const [phase, setPhase] = useState<ScanPhase>("input");
  const [statusMessages, setStatusMessages] = useState<string[]>([]);
  const [scannedEndpoints, setScannedEndpoints] = useState<ScannedEndpoint[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [adding, setAdding] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const logEndRef = useRef<HTMLDivElement>(null);

  const reset = () => {
    setUrl("");
    setPhase("input");
    setStatusMessages([]);
    setScannedEndpoints([]);
    setSelected(new Set());
    setScanError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [statusMessages]);

  const handleScan = async () => {
    if (!url.trim()) return;
    setPhase("scanning");
    setStatusMessages([]);
    setScanError(null);
    setScannedEndpoints([]);

    try {
      const resp = await request("/api/v1/scan", "POST" as SimMethod, { urls: [url.trim()] });

      if (!resp.ok || !resp.body) {
        const msg = await resp.text().catch(() => `HTTP ${resp.status}`);
        setScanError(msg);
        setPhase("results");
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === "status") {
              setStatusMessages((prev) => [...prev, event.message]);
            } else if (event.type === "done") {
              const eps: ScannedEndpoint[] = (event.endpoints ?? []).filter(
                (e: any) => e && e.path && e.method
              );
              setScannedEndpoints(eps);
              setSelected(new Set(eps.map((_, i) => i)));
              setPhase("results");
            } else if (event.type === "error") {
              setScanError(event.message);
              setPhase("results");
            }
          } catch {
            // ignore malformed SSE lines
          }
        }
      }
    } catch (e: any) {
      setScanError(e?.message ?? "Unknown error");
      setPhase("results");
    }
  };

  const toggleAll = () => {
    if (selected.size === scannedEndpoints.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(scannedEndpoints.map((_, i) => i)));
    }
  };

  const toggleOne = (i: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const handleAdd = async () => {
    const toAdd = scannedEndpoints
      .filter((_, i) => selected.has(i))
      .map((ep) => ({
        method: (METHODS.includes(ep.method as Method) ? ep.method : "GET") as Method,
        path: ep.path,
        status_code: 200,
        response_headers: {},
        response_body: "",
        // Backend's bulk handler maps response_example → response_body when
        // no explicit body is provided. Letting the server own the mapping
        // keeps it consistent for any future caller (CLI/agent/etc).
        response_example: ep.response_example ?? "",
        description: ep.description ?? ep.name ?? "",
      }));
    setAdding(true);
    try {
      await onAdd(toAdd);
      handleClose();
    } finally {
      setAdding(false);
    }
  };

  const validMethod = (m: string): m is Method => METHODS.includes(m as Method);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Scan API documentation</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Documentation URL"
            size="small"
            fullWidth
            placeholder="https://api.example.com/docs"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={phase === "scanning"}
            onKeyDown={(e) => {
              if (e.key === "Enter" && phase === "input" && url.trim()) handleScan();
            }}
          />

          {/* Progress log */}
          {(phase === "scanning" || (phase === "results" && statusMessages.length > 0)) && (
            <Box
              sx={{
                bgcolor: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: 1,
                p: 1.5,
                maxHeight: 160,
                overflowY: "auto",
                fontFamily: "monospace",
                fontSize: 12,
              }}
            >
              {statusMessages.map((m, i) => (
                <Box key={i} sx={{ color: "#6b7280", lineHeight: 1.6 }}>
                  {m}
                </Box>
              ))}
              {phase === "scanning" && (
                <Box sx={{ color: "#7166cf", display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                  <CircularProgress size={10} sx={{ color: "#7166cf" }} />
                  <span>Scanning…</span>
                </Box>
              )}
              <div ref={logEndRef} />
            </Box>
          )}

          {scanError && <Alert severity="error">{scanError}</Alert>}

          {/* Results */}
          {phase === "results" && scannedEndpoints.length > 0 && (
            <>
              <Divider />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">
                  Found {scannedEndpoints.length} endpoint{scannedEndpoints.length !== 1 ? "s" : ""}
                </Typography>
                <Button size="small" onClick={toggleAll} sx={{ textTransform: "none", color: "#7166cf" }}>
                  {selected.size === scannedEndpoints.length ? "Deselect all" : "Select all"}
                </Button>
              </Stack>
              <Box
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 1,
                  maxHeight: 280,
                  overflowY: "auto",
                }}
              >
                {scannedEndpoints.map((ep, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 1.5,
                      py: 0.75,
                      borderBottom: i < scannedEndpoints.length - 1 ? "1px solid #f3f4f6" : "none",
                      "&:hover": { bgcolor: "#f9fafb" },
                      cursor: "pointer",
                    }}
                    onClick={() => toggleOne(i)}
                  >
                    <Checkbox
                      size="small"
                      checked={selected.has(i)}
                      onChange={() => toggleOne(i)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    <Chip
                      label={ep.method}
                      size="small"
                      color={validMethod(ep.method) ? METHOD_COLORS[ep.method as Method] : "default"}
                      sx={{ fontWeight: 700, minWidth: 60, justifyContent: "center", mr: 1 }}
                    />
                    <Box sx={{ flex: 1, fontFamily: "monospace", fontSize: 13, wordBreak: "break-all" }}>
                      {ep.path}
                    </Box>
                    {ep.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ maxWidth: 140, ml: 1 }}
                        noWrap
                      >
                        {ep.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        {phase === "input" && (
          <Button
            variant="contained"
            onClick={handleScan}
            disabled={!url.trim()}
            sx={{ bgcolor: "#7166cf", "&:hover": { bgcolor: "#5a4dbd" }, textTransform: "none" }}
          >
            Scan
          </Button>
        )}
        {phase === "scanning" && (
          <Button disabled sx={{ textTransform: "none" }}>
            <CircularProgress size={18} sx={{ mr: 1 }} />
            Scanning…
          </Button>
        )}
        {phase === "results" && scannedEndpoints.length > 0 && (
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={adding || selected.size === 0}
            sx={{ bgcolor: "#7166cf", "&:hover": { bgcolor: "#5a4dbd" }, textTransform: "none" }}
          >
            {adding ? <CircularProgress size={18} color="inherit" /> : `Add ${selected.size} endpoint${selected.size !== 1 ? "s" : ""}`}
          </Button>
        )}
        {phase === "results" && scannedEndpoints.length === 0 && !scanError && (
          <Button
            variant="outlined"
            onClick={reset}
            sx={{ textTransform: "none" }}
          >
            Try again
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const Endpoints: React.FC = () => {
  const { request } = useSimApi();
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await request("/api/v1/endpoints");
      if (res.ok) setEndpoints(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (ep: Endpoint) => {
    setEditingId(ep.id);
    setForm({
      method: ep.method,
      path: ep.path,
      status_code: ep.status_code,
      response_headers: ep.response_headers,
      response_body: ep.response_body,
      description: ep.description,
    });
    setError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = editingId
        ? await request(`/api/v1/endpoints/${editingId}`, "PUT", form)
        : await request("/api/v1/endpoints", "POST", form);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(JSON.stringify(body));
        return;
      }
      setDialogOpen(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this endpoint?")) return;
    await request(`/api/v1/endpoints/${id}`, "DELETE");
    await load();
  };

  const handleAddScanned = async (toAdd: Omit<Endpoint, "id">[]) => {
    await request("/api/v1/endpoints/bulk", "POST", toAdd);
    await load();
  };

  return (
    <Box sx={{ p: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Endpoints
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<AutoFixHighIcon />}
            onClick={() => setScanOpen(true)}
            sx={{ textTransform: "none", borderColor: "#7166cf", color: "#7166cf", "&:hover": { borderColor: "#5a4dbd", color: "#5a4dbd" } }}
          >
            Scan from URL
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ bgcolor: "#7166cf", "&:hover": { bgcolor: "#5a4dbd" }, textTransform: "none" }}
          >
            Add endpoint
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <CircularProgress size={24} />
      ) : endpoints.length === 0 ? (
        <Typography color="text.secondary">
          No endpoints configured. Add one to start simulating API responses.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {endpoints.map((ep) => {
            const isExpanded = expandedIds.has(ep.id);
            const hasDetails = !!(ep.description || ep.response_body);
            return (
              <Box
                key={ep.id}
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 1,
                }}
              >
                <Box
                  onClick={() => hasDetails && toggleExpand(ep.id)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    cursor: hasDetails ? "pointer" : "default",
                    "&:hover": hasDetails ? { bgcolor: "#fafafa" } : undefined,
                  }}
                >
                  <IconButton
                    size="small"
                    disabled={!hasDetails}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (hasDetails) toggleExpand(ep.id);
                    }}
                    sx={{ p: 0.25, visibility: hasDetails ? "visible" : "hidden" }}
                  >
                    {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                  </IconButton>
                  <Chip
                    label={ep.method}
                    size="small"
                    color={METHOD_COLORS[ep.method]}
                    sx={{ fontWeight: 700, minWidth: 64, justifyContent: "center" }}
                  />
                  <Box sx={{ flex: 1, fontFamily: "monospace", fontSize: 14 }}>
                    {ep.path}
                  </Box>
                  <Chip label={ep.status_code} size="small" variant="outlined" />
                  {ep.builtin ? (
                    <Chip label="built-in" size="small" sx={{ fontSize: 10, height: 18, bgcolor: "#f3f4f6", color: "#9ca3af" }} />
                  ) : (
                    <>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(ep); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(ep.id); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 2, pb: 2, pt: 0.5, pl: 7, borderTop: "1px solid #f3f4f6" }}>
                    {ep.description && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                          Description
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap", color: "#374151" }}>
                          {ep.description}
                        </Typography>
                      </Box>
                    )}
                    {ep.response_body && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                          Response body
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            mt: 0.5,
                            mb: 0,
                            p: 1.5,
                            bgcolor: "#f9fafb",
                            border: "1px solid #e5e7eb",
                            borderRadius: 1,
                            fontFamily: "monospace",
                            fontSize: 12,
                            color: "#374151",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            maxHeight: 320,
                            overflowY: "auto",
                          }}
                        >
                          {ep.response_body}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? "Edit endpoint" : "Add endpoint"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && (
              <Typography color="error" variant="caption">
                {error}
              </Typography>
            )}
            <Stack direction="row" spacing={2}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Method</InputLabel>
                <Select
                  label="Method"
                  value={form.method}
                  onChange={(e) => setForm({ ...form, method: e.target.value as Method })}
                >
                  {METHODS.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Path"
                size="small"
                fullWidth
                placeholder="/api/invoices/{id}"
                value={form.path}
                onChange={(e) => setForm({ ...form, path: e.target.value })}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Status code"
                size="small"
                type="number"
                sx={{ width: 120 }}
                value={form.status_code}
                onChange={(e) => setForm({ ...form, status_code: parseInt(e.target.value) || 200 })}
              />
              <TextField
                label="Description"
                size="small"
                fullWidth
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Stack>
            <TextField
              label="Response body"
              multiline
              rows={6}
              size="small"
              fullWidth
              placeholder={'{"id": "{{path.id}}", "status": "ok"}'}
              value={form.response_body}
              onChange={(e) => setForm({ ...form, response_body: e.target.value })}
              helperText="Supports {{path.X}}, {{query.X}}, {{body.X}}, {{header.X}}, {{random.uuid}}, {{random.int}}"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.path}
            sx={{ bgcolor: "#7166cf", "&:hover": { bgcolor: "#5a4dbd" }, textTransform: "none" }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scan dialog */}
      <ScanDialog
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onAdd={handleAddScanned}
        request={request}
      />
    </Box>
  );
};

export default Endpoints;
